import { useState, useEffect, useCallback } from 'react'
import { chatService, type Provider } from '../services/chatService'

export const useProviders = () => {
  const [providers, setProviders] = useState<Provider[]>([])
  const [currentProvider, setCurrentProvider] = useState<Provider | null>(null)
  const [isLoadingProviders, setIsLoadingProviders] = useState(false)

  const loadProviders = useCallback(async () => {
    setIsLoadingProviders(true)
    try {
      const [providersData, statusData] = await Promise.all([
        chatService.getProviders(),
        chatService.getProviderStatus()
      ])

      setProviders(providersData)
      
      // Try to restore from localStorage first
      const savedProvider = localStorage.getItem('selectedProvider')
      let restoredProvider: Provider | null = null
      
      if (savedProvider) {
        try {
          const parsed = JSON.parse(savedProvider)
          // Verify the saved provider still exists in the list
          restoredProvider = providersData.find(p => 
            p.provider === parsed.provider && p.model === parsed.model
          ) || null
        } catch (error) {
          console.warn('Failed to parse saved provider:', error)
        }
      }
      
      if (restoredProvider) {
        setCurrentProvider(restoredProvider)
      } else {
        // Find current provider from backend status
        const current = providersData.find(p => 
          p.provider === statusData.currentProvider && 
          p.model === statusData.currentModel
        )
        
        if (current) {
          setCurrentProvider(current)
        } else if (providersData.length > 0) {
          // Fallback to first available provider
          const firstAvailable = providersData.find(p => p.available)
          const fallbackProvider = firstAvailable || providersData[0]
          setCurrentProvider(fallbackProvider)
          // Save the fallback selection
          localStorage.setItem('selectedProvider', JSON.stringify(fallbackProvider))
        }
      }
    } catch (error) {
      console.error('Error loading providers:', error)
      // Set default fallback
      const defaultProvider: Provider = {
        provider: 'anthropic',
        model: 'claude-sonnet-4-5-20250929',
        available: true,
        status: 'Fallback provider'
      }
      setProviders([defaultProvider])
      setCurrentProvider(defaultProvider)
    } finally {
      setIsLoadingProviders(false)
    }
  }, [])

  const switchProvider = useCallback(async (provider: Provider) => {
    if (!provider.available) return

    setIsLoadingProviders(true)
    try {
      // For different providers, call the backend switch API
      if (currentProvider?.provider !== provider.provider) {
        await chatService.switchProvider(provider.provider, provider.model)
      }
      // Always update local state to maintain model selection
      setCurrentProvider(provider)
      
      // Store in localStorage for persistence
      localStorage.setItem('selectedProvider', JSON.stringify(provider))
      
    } catch (error) {
      console.error('Error switching provider:', error)
      // Even if the switch fails, update local state for testing
      setCurrentProvider(provider)
      localStorage.setItem('selectedProvider', JSON.stringify(provider))
    } finally {
      setIsLoadingProviders(false)
    }
  }, [currentProvider])

  const refreshProviders = useCallback(() => {
    loadProviders()
  }, [loadProviders])

  const clearProviderSelection = useCallback(() => {
    localStorage.removeItem('selectedProvider')
    // Reset to first available provider
    if (providers.length > 0) {
      const firstAvailable = providers.find(p => p.available) || providers[0]
      setCurrentProvider(firstAvailable)
    }
  }, [providers])

  // Load providers on mount
  useEffect(() => {
    loadProviders()
  }, [loadProviders])

  return {
    providers,
    currentProvider,
    isLoadingProviders,
    switchProvider,
    refreshProviders,
    loadProviders,
    clearProviderSelection
  }
}